import { FC, useCallback } from 'react';

interface WrapperProps {
  value: string;
}

/**
 * Custom hook for retrieving translations by key, with optional JSX wrapping.
 *
 * @param translations — current translation map (can be partial)
 * @param initial — fallback/default translation map (also partial)
 *
 * @returns A memoized function that takes a key and optional wrapper component.
 *          Returns either the raw string or a JSX element if a wrapper is provided.
 */
export const useTranslations = <T extends object>(translations: Partial<T>, initial: Partial<T>) => {
  // TODO: Add support for nested keys (e.g., translations.user.name)
  return useCallback(
    /**
     * @param key — translation key
     * @param Wrapper — optional React component to wrap the translation string
     *
     * @returns The translation string or a JSX-wrapped version of it
     */
    (key: keyof T, Wrapper?: FC<WrapperProps>) => {
      const value = key in translations ? translations[key] : key in initial ? initial[key] : null;

      if (!Wrapper || !value) {
        return value;
      }

      return <Wrapper value={value as string} />;
    },
    [translations, initial],
  );
};
